"use client";

import "photoswipe/style.css";
import "@/styles/image-gallery.css";

import { useCallback, useEffect, useRef, type ComponentProps } from "react";
import { downloadZip } from "client-zip";
import { useTranslations } from "next-intl";
import PhotoSwipe from "photoswipe";

import { cn } from "@/lib/utils";

type Picked = {
  src: string;
  width: number;
  height: number;
  alt: string;

  element: HTMLImageElement;
};

const ICON_ATTRS =
  'viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"';

const ICONS: Record<string, string> = {

  close: `<svg ${ICON_ATTRS}><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>`,

  zoom: `<svg ${ICON_ATTRS}><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/><path d="M11 8v6"/><path d="M8 11h6"/></svg>`,

  arrowPrev: `<svg ${ICON_ATTRS}><path d="m15 18-6-6 6-6"/></svg>`,

  arrowNext: `<svg ${ICON_ATTRS}><path d="m9 18 6-6-6-6"/></svg>`,

  preloader: `<svg ${ICON_ATTRS}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>`,
};

const ICON_DOWNLOAD =
  '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M7 10l5 5 5-5"/><path d="M12 15V3"/></svg>';

const ICON_PACKAGE =
  '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M16.5 9.4 7.5 4.21"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/></svg>';

export function ImageGallery({
  className,
  children,
  ...props
}: ComponentProps<"div">) {
  const t = useTranslations("site.ui");
  const scopeRef = useRef<HTMLDivElement>(null);

  const packingRef = useRef(false);
  const activeRef = useRef<PhotoSwipe | null>(null);
  useEffect(() => () => activeRef.current?.destroy(), []);

  const downloadAll = useCallback(async (items: Picked[]) => {
    if (items.length === 0 || packingRef.current) return;

    packingRef.current = true;

    try {
      const files = await Promise.all(
        items.map(async (item, i) => {
          const response = await fetch(item.src);
          const blob = await response.blob();

          const ext = (blob.type.split("/")[1] || "png").split("+")[0];

          return {
            name: `${String(i + 1).padStart(2, "0")}.${ext}`,
            input: blob,
          };
        }),
      );

      const blob = await downloadZip(files).blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `images-${Date.now()}.zip`;
      link.click();

      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("[image gallery] 打包下载失败", error);
    } finally {
      packingRef.current = false;
    }
  }, []);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const clicked = event.target as HTMLElement;

      const target = clicked.tagName === "IMG"
        ? clicked
        : clicked.closest("[data-gallery-trigger]")?.querySelector("img");

      if (!target || target.hasAttribute("data-no-zoom")) {
        return;
      }

      const scope = scopeRef.current;

      if (!scope || activeRef.current) return;

      const images = Array.from(
        scope.querySelectorAll<HTMLImageElement>("img:not([data-no-zoom])"),
      );
      const index = images.indexOf(target as HTMLImageElement);

      if (index === -1) return;

      const items: Picked[] = images.map((img) => {
        const rect = img.getBoundingClientRect();

        return {
          src: img.currentSrc || img.src,

          width: img.naturalWidth || Math.round(rect.width),
          height: img.naturalHeight || Math.round(rect.height),
          alt: img.alt,
          element: img,
        };
      });

      const pswp = new PhotoSwipe({

        appendToEl: scope.closest<HTMLElement>('[data-slot="dialog-content"]') ?? undefined,
        dataSource: items.map((item) => ({
          src: item.src,
          width: item.width,
          height: item.height,
          alt: item.alt || undefined,
          element: item.element,

          msrc: item.src,
        })),
        index,

        showHideAnimationType: "zoom",

        wheelToZoom: true,
        bgOpacity: 1,

        closeTitle: t("close"),
        zoomTitle: t("zoom"),
        arrowPrevTitle: t("previousImage"),
        arrowNextTitle: t("nextImage"),
        errorMsg: t("imageLoadFailed"),
        counter: items.length > 1,
        arrowPrev: items.length > 1,
        arrowNext: items.length > 1,
      });
      activeRef.current = pswp;
      pswp.on("destroy", () => { activeRef.current = null; });

      pswp.on("uiRegister", () => {
        pswp.ui?.registerElement({
          name: "download",
          order: 8,
          isButton: true,
          ariaLabel: t("download"),
          html: ICON_DOWNLOAD,
          onClick: () => {
            const current = items[pswp.currIndex];

            if (!current) return;

            const link = document.createElement("a");

            link.href = current.src;
            link.download = "";
            link.target = "_blank";
            link.rel = "noopener";
            link.click();
          },
        });

        if (items.length > 1) {
          pswp.ui?.registerElement({
            name: "download-all",
            order: 9,
            isButton: true,
            ariaLabel: t("downloadAll"),
            html: ICON_PACKAGE,
            onClick: () => void downloadAll(items),
          });
        }
      });

      pswp.addFilter("uiElement", (element, data) => {

        const icon = data.name ? ICONS[data.name] : undefined;
        const target = element.querySelector(".pswp__icn");

        if (icon && target) {

          target.outerHTML =
            data.name === "preloader"
              ? icon.replace("<svg ", '<svg class="pswp__icn" ')
              : icon;
        }

        return element;
      });

      pswp.on("openingAnimationStart", () => {
        pswp.element?.addEventListener("keydown", (event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            pswp.close();
          }
        });
        pswp.element
          ?.querySelectorAll("[title]")
          .forEach((node) => node.removeAttribute("title"));
      });

      pswp.init();
    },
    [downloadAll, t],
  );

  return (
    <div
      ref={scopeRef}
      onClick={handleClick}

      className={cn("[&_img:not([data-no-zoom])]:cursor-pointer", className)}
      {...props}
    >
      {children}
    </div>
  );
}
